import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lab = { fontSize: "11px", fontWeight: "bold", display: "block", marginBottom: "3px", color: "#333" };
const groupBox = {
  border: "1px solid #a0a0a0", borderRadius: "3px", padding: "10px 12px",
  background: "#f5f4ef", position: "relative", marginTop: "8px",
};
const groupTitle = {
  position: "absolute", top: "-8px", left: "8px", background: "#f5f4ef",
  padding: "0 6px", fontSize: "11px", fontWeight: "bold", color: "#003399",
};
const infoRow = { display: "flex", fontSize: "12px", padding: "3px 0" };
const infoLabel = { width: "150px", color: "#555", flexShrink: 0 };

// Les dates sont stockées en UTC (naïf) — conversion à l'heure de Montréal.
export const fmtDateMontreal = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).endsWith("Z") ? s : `${s}Z`);
  if (isNaN(d.getTime())) return String(s).replace("T", " ");
  return d.toLocaleString("fr-CA", {
    timeZone: "America/Montreal",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function FicheUtilisateurModal({ user, regions, onClose, onSaved }) {
  const [bureaux, setBureaux] = useState([]);
  const initialRoles = (user.roles && user.roles.length)
    ? user.roles
    : (user.role ? user.role.split(",").map(s => s.trim()).filter(Boolean) : ["employe"]);
  const [form, setForm] = useState({
    nom_affichage: user.nom_affichage || "",
    region: user.region || "",
    bureau: user.bureau || "",
    bureaux: user.bureaux || [],
    roles: initialRoles.length ? initialRoles : ["employe"],
    actif: user.actif !== false,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!form.region) { setBureaux([]); return; }
    axios.get(`${API}/reference/bureaux-by-region`, { params: { region: form.region }, withCredentials: true })
      .then(r => setBureaux(r.data || [])).catch(() => setBureaux([]));
  }, [form.region]);

  const toggleBureau = (code) => {
    setForm(f => ({
      ...f,
      bureaux: f.bureaux.includes(code) ? f.bureaux.filter(c => c !== code) : [...f.bureaux, code],
    }));
  };

  // « employe » est le rôle de base implicite ; « admin » implique tous les droits.
  const roleOptions = [
    { code: "supprimer", label: "Supprimer — supprimer une attestation ou un refus (onglet Liste)" },
    { code: "jumelage", label: "Jumelage — accès au bouton Jumelage (menu Outils)" },
    { code: "admin", label: "Admin — tous les droits" },
  ];
  const hasAdmin = form.roles.includes("admin");
  const roleChecked = (code) => hasAdmin || form.roles.includes(code);
  const toggleRole = (code) => {
    setForm(f => {
      let roles = new Set(f.roles);
      if (code === "admin") {
        if (roles.has("admin")) { roles.delete("admin"); }
        else { roles = new Set(["employe", "supprimer", "jumelage", "admin"]); }
      } else {
        roles.has(code) ? roles.delete(code) : roles.add(code);
      }
      roles.add("employe");
      return { ...f, roles: Array.from(roles) };
    });
  };

  const save = async () => {
    setSaving(true); setMessage("");
    // le bureau par défaut fait toujours partie des bureaux accessibles
    const payload = {
      ...form,
      bureaux: form.bureau
        ? [form.bureau, ...form.bureaux.filter(c => c !== form.bureau)]
        : form.bureaux,
    };
    try {
      const r = await axios.put(`${API}/utilisateurs/${user.id}`, payload, { withCredentials: true });
      setMessage("Fiche enregistrée.");
      onSaved(r.data);
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const regionLabel = (code) => {
    const r = regions.find(x => x.code === code);
    return r ? `${r.code}-${(r.Description || r.description || "").toUpperCase()}` : code || "—";
  };

  return (
    <div className="popup-overlay" onClick={e => { e.stopPropagation(); onClose(); }} style={{ zIndex: 1200 }}>
      <div
        className="etab-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 640, width: "92%", maxHeight: "92vh", overflowY: "auto" }}
        data-testid="fiche-utilisateur-modal"
      >
        <div className="popup-header" style={{ background: "#0033A0", padding: "6px 12px" }}>
          <span className="popup-title">Fiche utilisateur — {user.nom_affichage || user.courriel}</span>
          <button className="win-modal-close" onClick={onClose} data-testid="fiche-user-btn-x">×</button>
        </div>

        <div style={{ padding: "12px 16px 16px" }}>
          {/* Identité (lecture seule — provient de Microsoft Entra) */}
          <div style={groupBox}>
            <span style={groupTitle}>Identité (Microsoft Entra)</span>
            <div style={infoRow}>
              <span style={infoLabel}>Courriel</span>
              <span style={{ fontWeight: "bold" }} data-testid="fiche-user-courriel">{user.courriel || "—"}</span>
            </div>
            <div style={infoRow}>
              <span style={infoLabel}>Identifiant (oid)</span>
              <span style={{ fontFamily: "monospace", fontSize: "11px" }}>{user.oid || "—"}</span>
            </div>
            <div style={infoRow}>
              <span style={infoLabel}>Compte créé le</span>
              <span>{fmtDateMontreal(user.date_creation)}</span>
            </div>
            <div style={infoRow}>
              <span style={infoLabel}>Dernière connexion</span>
              <span>{fmtDateMontreal(user.derniere_connexion)}</span>
            </div>
          </div>

          {/* Profil modifiable */}
          <div style={{ ...groupBox, marginTop: "16px" }}>
            <span style={groupTitle}>Profil</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px" }}>
              <div>
                <label style={lab}>Nom d'affichage</label>
                <input
                  className="win-input" data-testid="fiche-user-input-nom"
                  value={form.nom_affichage}
                  onChange={e => setForm(f => ({ ...f, nom_affichage: e.target.value }))}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label style={lab}>Région par défaut</label>
                <select
                  className="win-select" data-testid="fiche-user-select-region"
                  value={form.region}
                  onChange={e => setForm(f => ({ ...f, region: e.target.value, bureau: "", bureaux: [] }))}
                  style={{ width: "100%" }}
                >
                  <option value="">-- Aucune --</option>
                  {regions.map(r => (
                    <option key={r.code} value={r.code}>{regionLabel(r.code)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lab}>Bureau par défaut</label>
                <select
                  className="win-select" data-testid="fiche-user-select-bureau"
                  value={form.bureau}
                  onChange={e => setForm(f => ({ ...f, bureau: e.target.value }))}
                  style={{ width: "100%" }}
                  disabled={!form.region}
                >
                  <option value="">-- Aucun --</option>
                  {bureaux.map(b => (
                    <option key={b.code} value={b.code}>
                      {b.code} - {b.bureau || b.Description || b.description || ""}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lab}>Rôles (accès aux fonctions)</label>
                <div data-testid="fiche-user-roles"
                     style={{ display: "flex", flexDirection: "column", gap: "4px",
                              background: "#fff", border: "1px solid #a0a0a0",
                              padding: "8px 10px", borderRadius: "3px" }}>
                  <label style={{ fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px", color: "#777" }}>
                    <input type="checkbox" checked disabled data-testid="fiche-user-role-employe" />
                    Employé — accès de base (attribué à toute connexion SSO)
                  </label>
                  {roleOptions.map(opt => (
                    <label key={opt.code}
                           style={{ fontSize: "12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <input
                        type="checkbox"
                        data-testid={`fiche-user-role-${opt.code}`}
                        checked={roleChecked(opt.code)}
                        disabled={hasAdmin && opt.code !== "admin"}
                        onChange={() => toggleRole(opt.code)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: "10px", color: "#888", marginTop: "2px" }}>
                  « Admin » sélectionne automatiquement tous les droits.
                </div>
              </div>
              <div>
                <label style={lab}>Compte actif</label>
                <label style={{ fontSize: "12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                  <input
                    type="checkbox" data-testid="fiche-user-check-actif"
                    checked={form.actif}
                    onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))}
                  />
                  {form.actif ? "Oui — peut se connecter" : "Non — connexion refusée"}
                </label>
              </div>
            </div>

            {form.region && bureaux.filter(b => b.code !== form.bureau).length > 0 && (
              <div style={{ marginTop: "12px" }}>
                <label style={lab}>Bureaux supplémentaires accessibles</label>
                <div style={{
                  display: "flex", gap: "10px 16px", flexWrap: "wrap", background: "#fff",
                  border: "1px solid #a0a0a0", padding: "8px", maxHeight: "120px", overflowY: "auto",
                }}>
                  {bureaux.filter(b => b.code !== form.bureau).map(b => (
                    <label key={b.code} style={{ fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>
                      <input
                        type="checkbox"
                        data-testid={`fiche-user-bureau-${b.code}`}
                        checked={form.bureaux.includes(b.code)}
                        onChange={() => toggleBureau(b.code)}
                        style={{ marginRight: "4px" }}
                      />
                      {b.code} - {b.bureau || b.Description || b.description || ""}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: "14px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px" }}>
            {message && (
              <span style={{ fontSize: "12px", color: message.startsWith("Erreur") ? "#b00000" : "#007000", marginRight: "auto" }}
                    data-testid="fiche-user-message">{message}</span>
            )}
            <button className="win-btn" onClick={onClose} data-testid="fiche-user-close-btn">Fermer</button>
            <button className="win-btn" onClick={save} disabled={saving}
                    style={{ fontWeight: "bold" }} data-testid="fiche-user-save-btn">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
